import React from "react";
import ReactDOM from "react-dom";
import Pisco from "pisco-ui";
import "pisco-ui/lib/styles.css";
import "./pisco-readonly.css";
import { searchMedia } from "../media-library/media-library-api.js";
import {
  mediaItemToContent,
  quickAddToContent
} from "./normalizers.js";

const PAGE = 30;

// Unified image picker for the article editor. A drop-in replacement for the
// embedded <Pisco> component: it accepts the same essential props and emits the
// exact pisco `content` shape via onEmbed, so story-page-content.jsx and
// tinymce-editor.jsx stay unchanged downstream.
//
// Tabs: Media Library (new catalog, search-only) · Pisco (legacy) · + Upload
// (article-local upload — inserted into THIS article only, NEVER written to the
// Media Library catalog or pisco; covers still route through the downstream
// 1200x630 cover crop). Catalog uploads live on the Media tab.
//
// The Pisco tab renders the REAL pisco-ui <Pisco> component (mode="embed") — the
// SAME component the standalone sidebar library uses — so the article's pisco
// search + filter menu (Getty/Elastic source toggles + Sort By) is IDENTICAL to
// the sidebar by construction. Picking a pisco-library result embeds it directly
// (no write); picking a Getty result routes through the same save/import flow the
// sidebar uses, which the migration blocks server-side (403) — so no new image is
// ever written to pisco (the pisco-readonly.css import also hides the "Add to
// Library" button whenever REACT_APP_PISCO_UPLOAD_DISABLED is on).
export default class UnifiedImagePicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: props.defaultTab || "media",
      q: "",
      mediaItems: [],
      mediaPage: 1,
      mediaTotal: 0,
      loading: false,
      error: null,
      qaCaption: "",
      qaCredit: "",
      qaBusy: false
    };
    this._searchTimer = null;
    this._qaInput = null;
    this._unmounted = false;

    this.onSearchChange = this.onSearchChange.bind(this);
    this.emit = this.emit.bind(this);
    this.doQuickAdd = this.doQuickAdd.bind(this);
  }

  componentDidMount() {
    this.fetchActive(true);
  }
  componentWillUnmount() {
    this._unmounted = true;
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  domainId() {
    return (this.props.injectQuery && this.props.injectQuery.domainId) || this.props.activeDomain;
  }
  authToken() {
    return (this.props.headers && this.props.headers.authorization) || localStorage.getItem("accessToken");
  }

  setTab(tab) {
    if (tab === this.state.tab) return;
    var self = this;
    this.setState({ tab: tab, error: null }, function () {
      // Only the Media tab searches through this component; the Pisco tab's
      // <Pisco> owns its own search + fetching.
      if (tab === "media") self.fetchActive(true);
    });
  }

  onSearchChange(e) {
    var q = e.target.value;
    this.setState({ q: q });
    if (this._searchTimer) clearTimeout(this._searchTimer);
    var self = this;
    this._searchTimer = setTimeout(function () {
      self.fetchActive(true);
    }, 350);
  }

  fetchActive(reset) {
    // The Pisco tab renders the real <Pisco>, which owns its own search bar +
    // fetching, so only the Media Library tab searches through this component.
    if (this.state.tab === "media") this.fetchMedia(reset);
  }

  fetchMedia(reset) {
    var self = this;
    var page = reset ? 1 : this.state.mediaPage + 1;
    this.setState({ loading: true, error: null });
    searchMedia(this.domainId(), { q: this.state.q || undefined, mediaType: "image", page: page, limit: PAGE })
      .then(function (res) {
        if (self._unmounted) return;
        var items = (res && res.items) || [];
        var total = (res && res.pagination && res.pagination.total) || 0;
        self.setState({
          loading: false,
          mediaItems: reset ? items : self.state.mediaItems.concat(items),
          mediaPage: page,
          mediaTotal: total
        });
      })
      .catch(function (err) {
        if (self._unmounted) return;
        var status = err && err.response && err.response.status;
        var msg =
          status === 503 ? "Media Library is turned off for the old CMS. Use the Pisco tab."
            : status === 404 ? "Media Library backend isn't enabled. Use the Pisco tab."
              : "Couldn't load the Media Library.";
        self.setState({ loading: false, error: msg, mediaItems: reset ? [] : self.state.mediaItems });
      });
  }

  emit(content) {
    // Provenance logging is the onEmbed handler's job (handleEmbed in
    // story-page-content.jsx) — it is the single funnel for BOTH this picker and
    // the embedded <Pisco>, and reads content.__source. Logging here too would
    // double-count, so the picker only forwards + closes.
    if (this.props.onEmbed) this.props.onEmbed(content);
    if (this.props.onClose) this.props.onClose();
  }

  doQuickAdd(file) {
    if (!file) return;
    var self = this;
    // Upload target id: `uploadId` (any context, e.g. a profile picture)
    // overrides the article id; both POST to the SAME public, no-catalog asset
    // endpoint (/article/:domainId/asset/:id — ACL public-read, no library/pisco write).
    var uploadId = this.props.uploadId || this.props.articleId;
    if (!uploadId) {
      window.alert("Please save the article before adding an image.");
      return;
    }
    // Keep in sync with absinthe MAX_ASSET_SIZE (config.js = 5 * 1e6). A file
    // between 5,000,000 and 5,242,880 would pass a 5*1024*1024 guard and then
    // 403 server-side with only a generic failure.
    if (file.size > 5000000) {
      window.alert("Image too big — max 5MB.");
      return;
    }
    var base = process.env.REACT_APP_API_SERVER || "";
    // path includes '/article'; multer field name is 'asset'; response is { result }
    var url = base + "/article/" + this.domainId() + "/asset/" + uploadId;
    var fd = new FormData();
    fd.append("asset", file);
    this.setState({ qaBusy: true });
    fetch(url, { method: "POST", headers: { authorization: this.authToken() }, body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error("upload failed: " + r.status);
        return r.json();
      })
      .then(function (json) {
        if (self._unmounted) return;
        var src = json && json.result;
        if (!src) throw new Error("no url in response");
        // Batch the qaBusy reset + emit (setCoverPictureUrl / setArticleCoverCaption
        // / hidePiscoModal) into ONE commit. React 16 does not batch updates inside
        // a promise callback, so without this the cover-set (which opens the crop
        // modal) and the pane-close land in separate renders and race — leaving the
        // picker open and the crop flashing behind it. The synchronous search-pick
        // path is already batched by the click handler, which is why it works.
        ReactDOM.unstable_batchedUpdates(function () {
          self.setState({ qaBusy: false });
          self.emit(quickAddToContent(src, { caption: self.state.qaCaption, credit: self.state.qaCredit }));
        });
      })
      .catch(function (err) {
        if (self._unmounted) return;
        self.setState({ qaBusy: false });
        console.error("quick-add failed", err);
        window.alert("Upload failed — please try again.");
      });
  }

  // ---- render helpers ----
  renderCard(key, thumb, title, credit, onClick) {
    return (
      <div key={key} onClick={onClick}
        style={{ cursor: "pointer", border: "1px solid #e0e0e0", borderRadius: 4, overflow: "hidden", background: "#fff" }}>
        <div style={{ width: "100%", height: 120, background: "#f4f4f4", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {thumb
            ? <img src={thumb} alt={title || ""} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
            : <span style={{ color: "#bbb", fontSize: 12 }}>no preview</span>}
        </div>
        <div style={{ padding: "6px 8px" }}>
          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "Untitled"}</div>
          {credit ? <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{credit}</div> : null}
        </div>
      </div>
    );
  }

  renderGrid(children) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginTop: 12 }}>
        {children}
      </div>
    );
  }

  render() {
    var self = this;
    var tab = this.state.tab;
    var tabBtn = function (id, label) {
      return (
        <button
          onClick={function () { self.setTab(id); }}
          style={{
            padding: "6px 14px", marginRight: 6, cursor: "pointer",
            border: "1px solid " + (tab === id ? "#e91e63" : "#ddd"),
            background: tab === id ? "#e91e63" : "#fff",
            color: tab === id ? "#fff" : "#333",
            borderRadius: 4, fontSize: 13
          }}
        >{label}</button>
      );
    };

    return (
      <div style={{ padding: 12 }}>
        <div style={{ marginBottom: 10 }}>
          {tabBtn("media", "Media Library")}
          {tabBtn("pisco", "Pisco (legacy)")}
          {tabBtn("upload", "+ Upload")}
        </div>

        {/* Media tab has its own search box here; the Pisco tab uses <Pisco>'s
            own search bar + filter menu, so no shared input is shown for it. */}
        {tab === "media" ? (
          <input
            type="text"
            value={this.state.q}
            onChange={this.onSearchChange}
            placeholder="Search Media Library…"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 14 }}
          />
        ) : null}

        {this.state.error ? (
          <div style={{ marginTop: 12, padding: 10, background: "#fff3f3", border: "1px solid #f2c2c2", color: "#a33", borderRadius: 4, fontSize: 13 }}>
            {this.state.error}
          </div>
        ) : null}

        {tab === "media" ? (
          <div>
            {this.renderGrid(this.state.mediaItems.map(function (it, i) {
              var thumb = it.thumbUrl || it.webUrl || it.imageUrl || it.url;
              return self.renderCard("m" + (it.publicId || i), thumb, it.title, it.creditLine || it.photographer,
                function () { self.emit(mediaItemToContent(it, self.props.isCover)); });
            }))}
            {!this.state.loading && !this.state.error && this.state.mediaItems.length === 0 ? emptyRow() : null}
            {this.state.mediaItems.length < this.state.mediaTotal ? loadMore(this.state.loading, function () { self.fetchMedia(false); }) : null}
          </div>
        ) : null}

        {tab === "pisco" ? (
          // The real pisco-ui component — identical search + filter menu (Getty /
          // Elastic source toggles + Sort By) to the standalone sidebar library.
          // Picks funnel through emit() -> onEmbed + provenance log + close.
          <div style={{ marginTop: 4 }}>
            <Pisco
              endpoint={this.props.endpoint || process.env.REACT_APP_API_SERVER}
              headers={{ authorization: this.authToken() }}
              injectQuery={this.props.injectQuery}
              disableGetty={this.props.disableGetty}
              type={this.props.type || "Image"}
              mode="embed"
              onEmbed={this.emit}
            />
          </div>
        ) : null}

        {tab === "upload" ? (
          <div style={{ marginTop: 12, maxWidth: 480 }}>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
              {this.props.uploadNote ? this.props.uploadNote : (
                <span>
                  Upload an image for <strong>this article only</strong>. It is inserted into the article and is <strong>not</strong> added to the Media Library or Pisco.
                  {this.props.isCover ? " You’ll crop it to the cover size (min 1200×630) next." : ""}
                </span>
              )}
            </p>
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Caption (optional)</label>
            <input type="text" value={this.state.qaCaption} onChange={function (e) { self.setState({ qaCaption: e.target.value }); }}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 8 }} />
            <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>Credit / photographer (optional)</label>
            <input type="text" value={this.state.qaCredit} onChange={function (e) { self.setState({ qaCredit: e.target.value }); }}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, marginBottom: 12 }} />
            <input type="file" accept="image/*" style={{ display: "none" }}
              ref={function (el) { self._qaInput = el; }}
              onChange={function (e) { var f = e.target.files && e.target.files[0]; e.target.value = null; self.doQuickAdd(f); }} />
            <button
              disabled={this.state.qaBusy}
              onClick={function () { if (self._qaInput) self._qaInput.click(); }}
              style={{ padding: "8px 16px", background: this.state.qaBusy ? "#ccc" : "#e91e63", color: "#fff", border: "none", borderRadius: 4, cursor: this.state.qaBusy ? "default" : "pointer" }}
            >{this.state.qaBusy ? "Uploading…" : "Choose image & insert"}</button>
          </div>
        ) : null}

        {this.state.loading && tab === "media" ? (
          <div style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading…</div>
        ) : null}
      </div>
    );
  }
}

function emptyRow() {
  return <div style={{ marginTop: 16, color: "#999", fontSize: 13 }}>No results.</div>;
}
function loadMore(loading, onClick) {
  return (
    <div style={{ textAlign: "center", marginTop: 16 }}>
      <button onClick={onClick} disabled={loading}
        style={{ padding: "6px 16px", border: "1px solid #ccc", background: "#fff", borderRadius: 4, cursor: "pointer" }}>
        {loading ? "Loading…" : "Load more"}
      </button>
    </div>
  );
}
